import React, { useState, useEffect } from 'react';
import TeamV2MembersNavbar from '../components/dashboard/TeamV2MembersNavbar';
import TeamV2MembersSelectionBar from '../components/dashboard/TeamV2MembersSelectionBar';
import ProgressUI from 'components/dashboard/ProgressUI';
import TeamV2CardContainer from '../components/dashboard/TeamV2CardContainer';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { v4 as uuidv4 } from 'uuid';

const TeamV2MembersContainer = ({ user }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCards, setSelectedCards] = useState(new Set());
    const tempCardId = uuidv4();
    const navigate = useNavigate();
    const location = useLocation();
    const { clearAllContactLinksData } = useContactLinkContext();

    // Notification state
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Check for notification data passed from navigation
    useEffect(() => {
        if (location.state?.notification) {
            setNotification({
                open: true,
                message: location.state.notification.message,
                severity: location.state.notification.severity
            });

            // Clear the state so it doesn't show again on refresh/navigation
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    const handleCreateCardClick = () => {
        console.log('Create New Card clicked!');
        // ✅ FIXED: Clear contact link context before starting new card creation
        clearAllContactLinksData();
        console.log('🔍 MyCardsContainer: Cleared contact link context for new card creation');
        navigate(`/dashboard/mycards/add-card/${tempCardId}`);
    };

    const handleEditCardClick = (card) => {
        console.log('Card Object:', card);
        navigate(`/dashboard/mycards/edit/${card._id}`);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleCardSelection = (cardId, isSelected) => {
        setSelectedCards(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(cardId);
            } else {
                newSet.delete(cardId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const allSelected = selectedCards.size === staticMembers.length;
        if (allSelected) {
            setSelectedCards(new Set());
        } else {
            setSelectedCards(new Set(staticMembers.map(m => m.id)));
        }
    };

    const handleAddMemberSelect = (option) => {
        console.log('Add member option selected:', option);
        // Navigate to the add member container - option will be handled internally
        navigate('/team-v2/add-member');
    };

    // Static members for now
    const staticMembers = [
        { id: '1', firstName: 'Alice', lastName: 'Johnson', email: 'alice@example.com', jobTitle: 'Designer', company: 'Acme Inc.', views: 128, leads: 12, devices: 3, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '2', firstName: 'Bob', lastName: 'Smith', email: 'bob@example.com', jobTitle: 'Engineer', company: 'Beta LLC', views: 256, leads: 24, devices: 5, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '3', firstName: 'Carol', lastName: 'Lee', email: 'carol@example.com', jobTitle: 'Product Manager', company: 'Gamma Co', views: 342, leads: 18, devices: 2, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '4', firstName: 'David', lastName: 'Ng', email: 'david@example.com', jobTitle: 'Sales', company: 'Delta Ltd', views: 87, leads: 8, devices: 1, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '5', firstName: 'Eve', lastName: 'Martinez', email: 'eve@example.com', jobTitle: 'Marketing', company: 'Acme Inc.', views: 154, leads: 10, devices: 4, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '6', firstName: 'Frank', lastName: 'O’Connor', email: 'frank@example.com', jobTitle: 'Engineer', company: 'Beta LLC', views: 220, leads: 16, devices: 2, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '7', firstName: 'Grace', lastName: 'Kim', email: 'grace@example.com', jobTitle: 'Designer', company: 'Gamma Co', views: 300, leads: 21, devices: 6, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '8', firstName: 'Hank', lastName: 'Wong', email: 'hank@example.com', jobTitle: 'Support', company: 'Delta Ltd', views: 65, leads: 5, devices: 1, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '9', firstName: 'Ivy', lastName: 'Patel', email: 'ivy@example.com', jobTitle: 'HR', company: 'Acme Inc.', views: 78, leads: 7, devices: 2, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '10', firstName: 'Jake', lastName: 'Brown', email: 'jake@example.com', jobTitle: 'Engineer', company: 'Beta LLC', views: 410, leads: 30, devices: 7, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '11', firstName: 'Kara', lastName: 'Singh', email: 'kara@example.com', jobTitle: 'Finance', company: 'Gamma Co', views: 92, leads: 9, devices: 2, avatarUrl: '', cardTheme: '#F5F5F5' },
        { id: '12', firstName: 'Liam', lastName: 'Davis', email: 'liam@example.com', jobTitle: 'Ops', company: 'Delta Ltd', views: 188, leads: 14, devices: 3, avatarUrl: '', cardTheme: '#F5F5F5' }
    ];

    return (
        <div>
            <TeamV2MembersNavbar
                onSearch={handleSearch}
                membersCount={staticMembers.length}
                onAddMemberSelect={handleAddMemberSelect}
            />

            <TeamV2MembersSelectionBar
                allSelected={selectedCards.size === staticMembers.length}
                onToggleAll={handleSelectAll}
                selectedCount={selectedCards.size}
                totalCount={staticMembers.length}
            />

            <TeamV2CardContainer
                items={staticMembers}
                searchQuery={searchQuery}
                selectedCards={selectedCards}
                onCardSelection={handleCardSelection}
            />

            {/* Notification Snackbar */}
            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />
        </div>
    );
};

export default TeamV2MembersContainer;